// ChangePasswordForm.tsx

import React, { useState } from 'react';
import { TextField, Button, Box, Typography } from '@mui/material';
import { axiosRequest } from '../../service/AxiosService';
import CustomMenu from "../../components/CustomMenu/CustomMenu";

/**
 * パスワード変更フォームコンポーネント
 * - 現在のパスワードと新しいパスワードを入力し、変更処理を実行する
 * - パスワードポリシー（8文字以上・数字・大小文字・記号すべて含む）のバリデーションを行う
 */
export const ChangePasswordForm: React.FC = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  /**
   * パスワードがポリシーを満たしているか検証する
   * @param password - 検証するパスワード
   * @returns ポリシーを満たす場合はtrue
   */
  const isValidPassword = (password: string) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return regex.test(password);
  };

  const handleNewPasswordBlur = () => {
    if (!isValidPassword(newPassword)) {
      setPasswordError('8文字以上で、数字・大文字・小文字・記号をすべて含めてください。');
    } else {
      setPasswordError('');
    }

    // 確認用パスワードとの一致もチェック
    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmError('新パスワードと一致しません。');
    } else {
      setConfirmError('');
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (newPassword !== confirmPassword) {
      setConfirmError('新パスワードと一致しません。');
    } else {
      setConfirmError('');
    }
  };

  const handleSubmit = async () => {
    if (!isValidPassword(newPassword)) {
      setMessage('新パスワードの形式が正しくありません。');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('新パスワードが一致しません。');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await axiosRequest<boolean>(
        '/changePassword',
        'POST',
        {
          data: {
            oldPassword: oldPassword,
            newPassword: newPassword,
          }
        },
      );

      if (response) {
        setMessage('パスワードを変更しました。');
      } else {
        setMessage('パスワード変更に失敗しました。');
      }
    } catch (error) {
      console.error(error);
      setMessage('エラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <CustomMenu>
      <Box display="flex" justifyContent="center">
      <Box maxWidth={400} onKeyDown={handleKeyDown} sx={{ mx: 2, mt: 5, }}>
        <Typography variant="h6" gutterBottom>
          パスワード変更
        </Typography>

        <TextField
          label="旧パスワード"
          type="password"
          fullWidth
          margin="normal"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
        />

        <Typography variant="body2" color="textSecondary" mt={2}>
          ※ 新しいパスワードは8文字以上で、数字・大文字・小文字・記号をすべて含めてください。
        </Typography>
        <TextField
          label="新パスワード"
          type="password"
          fullWidth
          margin="normal"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onBlur={handleNewPasswordBlur}
          error={!!passwordError}
          helperText={passwordError}
        />

        <TextField
          label="新パスワード（確認）"
          type="password"
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onBlur={handleConfirmPasswordBlur}
          error={!!confirmError}
          helperText={confirmError}
        />

        <Box sx={{ textAlign: "right" }}>
          <Button
            variant="contained"
            color="primary"
            // fullWidth
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? '変更中...' : '変更する'}
          </Button>
        </Box>

        {message && (
          <Typography
            variant="body2"
            color={message.includes('変更しました') ? 'success.main' : 'error'}
            mt={2}
          >
            {message}
          </Typography>
        )}
      </Box></Box>
    </CustomMenu>
  );
};
